import {
  ArrowRight,
  Bot,
  BriefcaseBusiness,
  CalendarCheck,
  CheckCircle2,
  Compass,
  Flame,
  FileText,
  GraduationCap,
  Map,
  Medal,
  Trophy,
  Route,
  Sparkles,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getGrowthSummary } from '../api.js'
import { useAuth } from '../auth-context.js'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { setPageMetadata } from '../page-metadata.js'
import { hasEffectiveGrowthData } from '../student-growth-state.js'

const moduleItems = [
  { titleKey: 'homeGrowth.moduleExploreTitle', descKey: 'homeGrowth.moduleExploreDesc', to: '/explore', icon: Compass, tone: 'violet' },
  { titleKey: 'homeGrowth.moduleLearningTitle', descKey: 'homeGrowth.moduleLearningDesc', to: '/learning', icon: Map, tone: 'green' },
  { titleKey: 'homeGrowth.moduleProjectTitle', descKey: 'homeGrowth.moduleProjectDesc', to: '/projects', icon: BriefcaseBusiness, tone: 'orange' },
  { titleKey: 'homeGrowth.moduleCareerTitle', descKey: 'homeGrowth.moduleCareerDesc', to: '/more/resume', icon: FileText, tone: 'blue' },
]

function cleanText(value, fallback = '') {
  if (typeof value === 'string') {
    const text = value.trim()
    return text && text !== '[object Object]' ? text : fallback
  }
  if (value && typeof value === 'object') {
    return cleanText(value.title || value.name || value.task || value.description, fallback)
  }
  return fallback
}

function QingTuHeroMark() {
  return (
    <div className="qingtu-hero-mark" aria-hidden="true">
      <img src="/brand/qingtu-logo.png" alt="" />
    </div>
  )
}

function normalizeHomeSummary(summary, t) {
  const currentGoal = cleanText(summary?.currentGoal?.title, t('homeGrowth.emptyDirection'))
  const currentStage = cleanText(summary?.growthStage?.stage, t('homeGrowth.emptyStage'))
  const tasks = Array.isArray(summary?.todayTasks?.items) ? summary.todayTasks.items : []
  const nextTask = tasks.find((task) => task.status !== 'completed') || tasks[0]
  const nextAdvice = cleanText(
    nextTask?.title || summary?.nextStep?.title,
    summary?.currentGoal?.title ? t('homeGrowth.defaultAdvice') : t('homeGrowth.emptyAdvice'),
  )
  const progress = Number(summary?.growthStage?.progress || 0)
  const taskSummary = summary?.todayTasks?.summary || {}
  const learningProgress = summary?.learningProgress || {}
  const projectAssets = summary?.projectAssets || {}
  const resumeAssets = summary?.resumeAssets || {}
  const totalTasks = Number(taskSummary.total || tasks.length || 0)
  const completedTasks = Number(taskSummary.completed || tasks.filter((task) => task.status === 'completed').length || 0)
  const projectTotal = Number(projectAssets.total || 0)
  const resumeTotal = Number(resumeAssets.total || 0)
  const certificateTotal = Number(summary?.certificateAssets?.total || 0)
  return {
    hasData: hasEffectiveGrowthData(summary) || tasks.length > 0,
    currentGoal,
    currentStage,
    nextAdvice,
    progress,
    completedTasks,
    continuousDays: Number(summary?.growthStage?.streakDays || summary?.profile?.streakDays || 0),
    projectTotal,
    certificateTotal: certificateTotal || resumeTotal,
    taskCount: tasks.length,
    todayTasks: tasks.slice(0, 3),
    totalTasks,
    learningPercent: Number(learningProgress.percent || 0),
  }
}

function AiPartnerStatusCard({ disabled, onPrimaryClick, primaryLabel, state, t }) {
  return (
    <aside className="qingtu-partner-card" aria-label={t('homeGrowth.partnerCardTitle')}>
      <div className="qingtu-partner-card-head">
        <span><Bot size={18} /> {t('homeGrowth.partnerCardTitle')}</span>
        <small>{state.hasData ? t('homeGrowth.partnerUpdated') : t('homeGrowth.partnerGuide')}</small>
      </div>
      <div className="qingtu-partner-bubble">
        <Sparkles size={18} />
        <p>{state.hasData ? t('homeGrowth.partnerObservation', { direction: state.currentGoal }) : t('homeGrowth.partnerEmptyObservation')}</p>
      </div>
      <dl className="qingtu-partner-state">
        <div>
          <dt>{t('homeGrowth.statusDirection')}</dt>
          <dd>{state.currentGoal}</dd>
        </div>
        <div>
          <dt>{t('homeGrowth.statusStage')}</dt>
          <dd>{state.currentStage}</dd>
        </div>
        <div className="is-wide">
          <dt>{t('homeGrowth.statusAdvice')}</dt>
          <dd>{state.nextAdvice}</dd>
        </div>
      </dl>
      <div className="qingtu-progress-line" aria-label={t('homeGrowth.statusProgress')}>
        <span style={{ width: `${Math.max(8, Math.min(100, state.progress || 12))}%` }} />
      </div>
      <button className="primary-button" type="button" onClick={onPrimaryClick} disabled={disabled}>
        {primaryLabel} <ArrowRight size={16} />
      </button>
    </aside>
  )
}

function HeroCompanionScene({ disabled, onPrimaryClick, primaryLabel, state, t }) {
  const progress = Math.max(8, Math.min(100, state.progress || (state.hasData ? 32 : 12)))

  return (
    <div className="qingtu-hero-scene" aria-label={t('homeGrowth.heroSceneLabel')}>
      <div className="qingtu-pet-speech">
        <strong>{t('homeGrowth.petSpeechTitle')}</strong>
        <span>{state.hasData ? t('homeGrowth.petSpeechReady') : t('homeGrowth.petSpeechEmpty')}</span>
      </div>
      <div className="qingtu-hero-pet" aria-hidden="true">
        <img src="/pets/cat/hero-cutout.png" alt="" loading="eager" />
      </div>
      <AiPartnerStatusCard disabled={disabled} onPrimaryClick={onPrimaryClick} primaryLabel={primaryLabel} state={state} t={t} />
      <aside className="qingtu-companion-mini">
        <div>
          <span>{t('homeGrowth.companionPanelTitle')}</span>
          <small>{state.hasData ? t('homeGrowth.companionPanelActive') : t('homeGrowth.companionPanelEmpty')}</small>
        </div>
        <ul>
          <li><Sparkles size={15} /> {t('homeGrowth.companionChat')}</li>
          <li><Route size={15} /> {t('homeGrowth.companionRecord')}</li>
          <li><Medal size={15} /> {t('homeGrowth.companionNurture')}</li>
        </ul>
      </aside>
      <div className="qingtu-hero-progress" aria-label={t('homeGrowth.statusProgress')}>
        <span>{t('homeGrowth.statusProgress')}</span>
        <strong>{progress}%</strong>
        <i><b style={{ width: `${progress}%` }} /></i>
      </div>
    </div>
  )
}

function HeroSection({ disabled, onPrimaryClick, primaryLabel, secondaryLabel, secondaryTo, summaryState, t }) {
  return (
    <section className="qingtu-home-hero">
      <div className="qingtu-hero-copy">
        <span className="qingtu-eyebrow"><Sparkles size={16} /> {t('homeGrowth.eyebrow')}</span>
        <QingTuHeroMark />
        <h1>
          <span>{t('homeGrowth.titleLine1')}</span>
          {' '}
          <span>{t('homeGrowth.titleLine2')}</span>
        </h1>
        <p>{t('homeGrowth.subtitle')}</p>
        <div className="qingtu-route-note">
          <Route size={18} />
          <span>{t('homeGrowth.routeIntro')}</span>
        </div>
        <div className="qingtu-hero-actions">
          <button className="primary-button" type="button" onClick={onPrimaryClick} disabled={disabled}>
            {primaryLabel} <ArrowRight size={18} />
          </button>
          <Link className="secondary-button" to={secondaryTo}>{secondaryLabel}</Link>
          <Link className="secondary-button" to="/ai-partner?intent=know-me">
            <Sparkles size={17} /> {t('homeGrowth.knowMe')}
          </Link>
        </div>
      </div>
      <HeroCompanionScene disabled={disabled} onPrimaryClick={onPrimaryClick} primaryLabel={primaryLabel} state={summaryState} t={t} />
    </section>
  )
}

function GrowthOverviewSection({ state, t }) {
  const stats = [
    { icon: Flame, label: t('homeGrowth.overviewStreak'), value: state.continuousDays ? t('homeGrowth.daysCount', { count: state.continuousDays }) : t('homeGrowth.notStarted'), tone: 'blue' },
    { icon: CheckCircle2, label: t('homeGrowth.overviewTasks'), value: `${state.completedTasks}/${state.totalTasks || 0}`, tone: 'green' },
    { icon: Trophy, label: t('homeGrowth.overviewProjects'), value: state.projectTotal, tone: 'violet' },
    { icon: Medal, label: t('homeGrowth.overviewCertificates'), value: state.certificateTotal, tone: 'orange' },
  ]

  return (
    <section className="qingtu-overview-section">
      <div>
        <span><Sparkles size={16} /> {t('homeGrowth.overviewLabel')}</span>
        <h2>{t('homeGrowth.overviewTitle')}</h2>
      </div>
      <div className="qingtu-overview-grid">
        {stats.map((item) => {
          const Icon = item.icon
          return (
            <article className={`is-${item.tone}`} key={item.label}>
              <Icon size={18} />
              <small>{item.label}</small>
              <strong>{item.value}</strong>
            </article>
          )
        })}
      </div>
    </section>
  )
}

function ModulesSection({ t }) {
  return (
    <section className="qingtu-section">
      <div className="qingtu-section-head">
        <span><GraduationCap size={16} /> {t('homeGrowth.modulesLabel')}</span>
        <h2>{t('homeGrowth.modulesTitle')}</h2>
        <p>{t('homeGrowth.modulesDesc')}</p>
      </div>
      <div className="qingtu-module-grid">
        {moduleItems.map((item) => {
          const Icon = item.icon
          return (
            <Link className={`qingtu-module-card is-${item.tone}`} to={item.to} key={item.titleKey}>
              <span className="qingtu-module-icon"><Icon size={22} /></span>
              <strong>{t(item.titleKey)}</strong>
              <p>{t(item.descKey)}</p>
              <span className="qingtu-module-link">{t('homeGrowth.moduleCta')} <ArrowRight size={14} /></span>
            </Link>
          )
        })}
      </div>
    </section>
  )
}

function TodayTaskSection({ state, disabled, onPrimaryClick, primaryLabel, t }) {
  const tasks = state.todayTasks.length
    ? state.todayTasks
    : [
        { id: 'empty-direction', title: t('homeGrowth.todayEmptyTask'), status: 'pending' },
      ]

  return (
    <section className="qingtu-today-section">
      <div>
        <span><CalendarCheck size={16} /> {t('homeGrowth.todayLabel')}</span>
        <h2>{t('homeGrowth.todayTitle')}</h2>
        <p>{state.hasData ? t('homeGrowth.todayDesc') : t('homeGrowth.todayEmptyDesc')}</p>
      </div>
      <div className="qingtu-today-list">
        {tasks.map((task) => (
          <article className={task.status === 'completed' ? 'is-done' : ''} key={task.id || task.title}>
            <CheckCircle2 size={17} />
            <span>{cleanText(task.title, t('homeGrowth.todayEmptyTask'))}</span>
          </article>
        ))}
      </div>
      <button className="primary-button" disabled={disabled} onClick={onPrimaryClick} type="button">
        {primaryLabel} <ArrowRight size={16} />
      </button>
    </section>
  )
}

export default function LandingPage() {
  const { language, t } = useLanguage()
  const auth = useAuth()
  const navigate = useNavigate()
  const [summaryState, setSummaryState] = useState({ checking: false, loaded: false, summary: null })
  const isLoggedIn = Boolean(auth?.isLoggedIn)
  const authLoading = Boolean(auth?.loading)

  useEffect(() => {
    setPageMetadata({
      title: t('homeGrowth.metaTitle'),
      description: t('homeGrowth.metaDescription'),
    })
  }, [t, language])

  useEffect(() => {
    if (authLoading) return undefined
    if (!isLoggedIn) {
      setSummaryState({ checking: false, loaded: true, summary: null })
      return undefined
    }
    const controller = new AbortController()
    setSummaryState((current) => ({ ...current, checking: true }))
    getGrowthSummary(controller.signal)
      .then((summary) => setSummaryState({ checking: false, loaded: true, summary }))
      .catch((error) => {
        if (error.name !== 'AbortError') {
          setSummaryState({ checking: false, loaded: true, summary: null })
        }
      })
    return () => controller.abort()
  }, [authLoading, isLoggedIn])

  const homeSummary = useMemo(() => normalizeHomeSummary(summaryState.summary, t), [summaryState.summary, t])

  function handlePrimaryClick() {
    if (authLoading || summaryState.checking) return
    navigate(homeSummary.hasData ? '/growth' : '/explore')
  }

  const primaryLabel = authLoading || summaryState.checking
    ? t('homeGrowth.checking')
    : homeSummary.hasData
      ? t('homeGrowth.continue')
      : t('homeGrowth.start')
  const primaryDisabled = authLoading || summaryState.checking
  const secondaryLabel = homeSummary.hasData ? t('homeGrowth.secondary') : t('homeGrowth.secondaryGuest')
  const secondaryTo = homeSummary.hasData ? '/explore' : '/projects'

  return (
    <main className="page-container qingtu-home">
      <HeroSection
        disabled={primaryDisabled}
        onPrimaryClick={handlePrimaryClick}
        primaryLabel={primaryLabel}
        secondaryLabel={secondaryLabel}
        secondaryTo={secondaryTo}
        summaryState={homeSummary}
        t={t}
      />
      <GrowthOverviewSection state={homeSummary} t={t} />
      <ModulesSection t={t} />
      <TodayTaskSection disabled={primaryDisabled} onPrimaryClick={handlePrimaryClick} primaryLabel={primaryLabel} state={homeSummary} t={t} />
    </main>
  )
}
